import React, { useEffect, useState } from 'react';
import { X } from 'react-feather';
import { Buyer, QuickbooksInvoice } from '@dill/dill-shared';
import { DillButton, DillInput, DillToggle } from '../Dill';
import { DillModal } from '../Dill/DillModal';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { sendInvoiceToCustomer } from '../../../redux/services/supplierBuyersService';
import { openDillToast } from '../../../utils/helpers';
import AppPhysicalDeliveryFields, {
  PhysicalDeliveryValue,
  getInitialPhysicalDelivery,
  toPhysicalDestinations,
} from '../AppPhysicalDeliveryFields/AppPhysicalDeliveryFields';

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// The customer's users who get invoice emails: everyone not opted out of invoices.
const getDefaultInvoiceEmails = (buyer: Buyer | null | undefined, userEmails: string[]) => {
  const optOut = new Set(
    (buyer?.invoiceOptOutEmails ?? []).map((email) => `${email}`.toLowerCase())
  );
  return [...new Set(userEmails.map((email) => email.toLowerCase()))].filter(
    (email) => email && !optOut.has(email)
  );
};

/**
 * Send Invoice: sends one invoice now - by email, mail and/or fax - e.g. to re-send after fixing a
 * delivery exception. Prefilled from the customer's invoice delivery settings; changes apply to
 * this send only. Not a payment reminder.
 */
const AppSendInvoiceModal = ({
  open,
  handleClose,
  invoice,
  buyer,
  buyerUserEmails,
}: {
  open: boolean;
  handleClose: () => void;
  invoice: QuickbooksInvoice | null;
  buyer: Buyer | null | undefined;
  buyerUserEmails: string[];
}) => {
  const dispatch = useAppDispatch();
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const canSendPhysically = !!currentSupplier?.isMailAndFaxDeliveryEnabled;
  const settings = buyer?.invoiceDeliverySettings;

  const [isEmailEnabled, setIsEmailEnabled] = useState(true);
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');
  const [physicalDelivery, setPhysicalDelivery] = useState<PhysicalDeliveryValue>(() =>
    getInitialPhysicalDelivery(settings)
  );
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setIsEmailEnabled(settings?.isEmailEnabled ?? !buyer?.isInvoiceNoticesDisabled);
    setEmails(getDefaultInvoiceEmails(buyer, buyerUserEmails));
    setEmailInput('');
    setEmailError('');
    setPhysicalDelivery(getInitialPhysicalDelivery(settings));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleAddEmail = () => {
    const email = emailInput.trim().toLowerCase();
    if (!isValidEmail(email)) {
      setEmailError('Enter a valid email address.');
      return;
    }
    if (emails.includes(email)) {
      setEmailError('This email is already in the list.');
      return;
    }
    setEmails((prev) => [...prev, email]);
    setEmailInput('');
    setEmailError('');
  };

  const destinations = toPhysicalDestinations(physicalDelivery);
  const emailsToSend = isEmailEnabled ? emails : [];
  const hasEmptyChannel =
    (isEmailEnabled && emails.length === 0) ||
    (canSendPhysically &&
      ((physicalDelivery.isMailEnabled && physicalDelivery.mailingAddresses.length === 0) ||
        (physicalDelivery.isFaxEnabled && physicalDelivery.faxNumbers.length === 0)));
  const destinationCount =
    emailsToSend.length +
    (canSendPhysically ? destinations.mailingAddresses.length + destinations.faxNumbers.length : 0);

  const handleSend = async () => {
    if (!invoice?.id || !buyer?.id || !currentSupplier?.id) return;
    setIsSending(true);
    const result = await dispatch(
      sendInvoiceToCustomer({
        supplierId: currentSupplier.id,
        buyerId: buyer.id,
        invoiceId: invoice.id,
        emails: emailsToSend,
        mailingAddresses: canSendPhysically ? destinations.mailingAddresses : [],
        faxNumbers: canSendPhysically ? destinations.faxNumbers : [],
      })
    );
    setIsSending(false);
    if (result.type === 'supplierBuyers/sendInvoiceToCustomer/fulfilled') {
      const data = (
        result.payload as {
          data?: { emailsSent: number; emailsFailed: number; physicalDeliveriesQueued: number };
        } | null
      )?.data;
      const parts = [
        data?.emailsSent ? `${data.emailsSent} email${data.emailsSent === 1 ? '' : 's'} sent` : '',
        data?.physicalDeliveriesQueued
          ? `${data.physicalDeliveriesQueued} mail / fax deliver${
              data.physicalDeliveriesQueued === 1 ? 'y' : 'ies'
            } queued`
          : '',
      ].filter(Boolean);
      openDillToast({
        type: data?.emailsFailed ? 'ERROR' : 'SUCCESS',
        message: `Invoice ${invoice.docNumber ? `#${invoice.docNumber} ` : ''}${parts.join(', ')}${
          data?.emailsFailed ? `; ${data.emailsFailed} email(s) failed` : ''
        }`,
      });
      handleClose();
    } else {
      openDillToast({
        type: 'ERROR',
        message: (result.payload as string) || 'Failed to send invoice',
      });
    }
  };

  return (
    <DillModal
      open={open}
      onClose={handleClose}
      title={`Send Invoice${invoice?.docNumber ? ` #${invoice.docNumber}` : ''}`}
      maxWidth="min(90vw, 600px)"
      footer={
        <>
          <DillButton label="Cancel" variant="secondary" onClick={handleClose} />
          <DillButton
            label="Send Invoice"
            onClick={handleSend}
            isLoading={isSending}
            isDisabled={isSending || hasEmptyChannel || destinationCount === 0}
          />
        </>
      }>
      <div className="space-y-4">
        <p className="text-xs text-gray-500">
          Sends this invoice now, from this customer&apos;s invoice delivery settings. Changes here
          apply to this send only. To ask for payment, use Send Payment Reminder instead.
        </p>

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Email</p>
            <DillToggle
              value={isEmailEnabled}
              onChange={setIsEmailEnabled}
              isDisabled={isSending}
            />
          </div>
          {isEmailEnabled && (
            <>
              {emails.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {emails.map((email) => (
                    <span
                      key={email}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-white border border-gray-200 rounded-full text-xs text-gray-700 shadow-sm">
                      {email}
                      <button
                        type="button"
                        aria-label={`Remove ${email}`}
                        onClick={() => setEmails((prev) => prev.filter((e) => e !== email))}
                        className="ml-0.5 text-gray-400 hover:text-gray-700 cursor-pointer bg-transparent border-none leading-none">
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  No recipients - add an email, or turn email off.
                </p>
              )}
              <div className="flex gap-2 items-start">
                <div className="flex-1">
                  <DillInput
                    value={emailInput}
                    onChange={(value) => {
                      setEmailInput(value);
                      setEmailError('');
                    }}
                    placeholder="Add email address"
                    error={emailError}
                    disabled={isSending}
                  />
                </div>
                <DillButton
                  label="Add"
                  variant="secondary"
                  height={34}
                  onClick={handleAddEmail}
                  isDisabled={isSending || !emailInput.trim()}
                />
              </div>
            </>
          )}
        </div>

        {canSendPhysically && (
          <AppPhysicalDeliveryFields
            value={physicalDelivery}
            onChange={setPhysicalDelivery}
            disabled={isSending}
          />
        )}
      </div>
    </DillModal>
  );
};

export default AppSendInvoiceModal;
